import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/financeiro')({ component: AdminFinanceiro });

const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente', PAID: 'Pago', REFUNDED: 'Reembolsado', FAILED: 'Falhou', TRANSFERRED: 'Transferido',
};
const CYCLE_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Aberto', CLOSED: 'Fechado',
  NF_PENDING: 'NF pendente', NF_REVIEW: 'NF em análise',
  NF_APPROVED: 'NF aprovada', NF_REJECTED: 'NF rejeitada',
  PAID: 'Pago', DISPUTED: 'Em disputa',
};

function AdminFinanceiro() {
  const [therapistId, setTherapistId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [cycleStatus, setCycleStatus] = useState('');
  const [method, setMethod] = useState('');
  const [page, setPage] = useState(1);

  const { data: therapists = [] } = useQuery({
    queryKey: ['admin-therapists'],
    queryFn: () => api.get('admin/therapists').json<any[]>(),
  });

  const filterArgs = { therapistId, paymentStatus, cycleStatus, method, page };
  const { data, isLoading } = useQuery({
    queryKey: ['admin-financial-report', filterArgs],
    queryFn: () => {
      const sp = new URLSearchParams();
      Object.entries(filterArgs).forEach(([k, v]) => v && sp.set(k, String(v)));
      return api.get(`admin/financial-report?${sp.toString()}`).json<{
        items: any[]; total: number; pages: number;
      }>();
    },
  });

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;
  const totalGross = items.reduce((s, x) => s + (x.amount ?? 0), 0);
  const totalFee = items.reduce((s, x) => s + (x.platformFee ?? 0), 0);
  const totalNet = items.reduce((s, x) => s + (x.netAmount ?? 0), 0);

  return (
    <>
      <AdminPageHeader title="Financeiro" subtitle={`${data?.total ?? '...'} pagamentos · página ${page}/${totalPages}`} />

      <section className="grid sm:grid-cols-3 gap-4 mb-6">
        <Kpi label="Bruto (página)" value={money(totalGross)} tone="slate" />
        <Kpi label="Receita Nosso Ello (página)" value={money(totalFee)} tone="coral" />
        <Kpi label="Repassado profissionais (página)" value={money(totalNet)} tone="teal" />
      </section>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 grid md:grid-cols-4 gap-3">
        <FilterSelect label="Profissional" value={therapistId} onChange={setTherapistId} options={[
          { value: '', label: 'Todos' },
          ...therapists.map((t: any) => ({ value: t.id, label: t.user?.name ?? t.id })),
        ]} />
        <FilterSelect label="Status pagamento" value={paymentStatus} onChange={setPaymentStatus} options={[
          { value: '', label: 'Todos' },
          ...Object.entries(PAYMENT_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
        ]} />
        <FilterSelect label="Status ciclo" value={cycleStatus} onChange={setCycleStatus} options={[
          { value: '', label: 'Todos' },
          ...Object.entries(CYCLE_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
        ]} />
        <FilterSelect label="Método" value={method} onChange={setMethod} options={[
          { value: '', label: 'Todos' },
          { value: 'CARD', label: 'Cartão' },
          { value: 'PIX', label: 'PIX' },
          { value: 'BOLETO', label: 'Boleto' },
        ]} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Profissional</th>
                <th className="text-left px-3 py-3 font-semibold">Cliente</th>
                <th className="text-left px-3 py-3 font-semibold">Tipo</th>
                <th className="text-left px-3 py-3 font-semibold">Data</th>
                <th className="text-left px-3 py-3 font-semibold">Sessão</th>
                <th className="text-left px-3 py-3 font-semibold">Método</th>
                <th className="text-right px-3 py-3 font-semibold">Bruto</th>
                <th className="text-right px-3 py-3 font-semibold">Taxa</th>
                <th className="text-right px-3 py-3 font-semibold">Líquido</th>
                <th className="text-left px-3 py-3 font-semibold">Pgto</th>
                <th className="text-left px-3 py-3 font-semibold">Ciclo</th>
                <th className="text-left px-3 py-3 font-semibold">Repasse</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={12} className="p-12 text-center text-sm text-slate-400">Carregando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={12} className="p-12 text-center text-sm text-slate-400">Nenhum pagamento.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50/60 text-xs">
                  <td className="px-3 py-2.5 text-slate-700 truncate max-w-[160px]">{it.therapistName ?? '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600 truncate max-w-[140px]">{it.clientName ?? '—'}</td>
                  <td className="px-3 py-2.5 text-slate-500 uppercase">{it.type === 'BATCH' ? 'Pacote' : 'Sessão'}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                    {it.scheduledAt ? new Date(it.scheduledAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500">{it.sessionStatus ?? '—'}</td>
                  <td className="px-3 py-2.5 text-slate-500 uppercase">{it.method ?? '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{money(it.amount)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-coral-600">- {money(it.platformFee)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold text-teal-700">{money(it.netAmount)}</td>
                  <td className="px-3 py-2.5">
                    <PaymentStatus status={it.status} />
                  </td>
                  <td className="px-3 py-2.5">
                    <CycleStatus status={it.cycleStatus} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                    {it.payoutExecutedAt ? new Date(it.payoutExecutedAt).toLocaleDateString('pt-BR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-slate-100 text-xs">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-teal-600 hover:underline disabled:text-slate-300 disabled:no-underline"
            >← Anterior</button>
            <span className="text-slate-500">Página {page} de {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="text-teal-600 hover:underline disabled:text-slate-300 disabled:no-underline"
            >Próxima →</button>
          </div>
        )}
      </div>
    </>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone: 'slate' | 'coral' | 'teal' }) {
  const cls =
    tone === 'coral' ? 'bg-coral-300 text-white' :
    tone === 'teal' ? 'bg-teal-300 text-white' :
    'bg-white border border-slate-100 text-slate-700';
  return (
    <div className={cn('rounded-3xl p-5', cls)}>
      <p className={cn('text-xs uppercase font-bold', tone === 'slate' ? 'text-slate-400' : 'opacity-80')}>{label}</p>
      <p className="text-2xl font-extrabold mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function PaymentStatus({ status }: { status: string }) {
  const cls =
    status === 'PAID' ? 'bg-green-50 text-green-700 border-green-200' :
    status === 'PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    status === 'REFUNDED' ? 'bg-orange-50 text-orange-700 border-orange-200' :
    status === 'FAILED' ? 'bg-red-50 text-red-600 border-red-200' :
    'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', cls)}>
      {PAYMENT_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function CycleStatus({ status }: { status: string | null }) {
  if (!status) return <span className="text-slate-300 text-[10px]">—</span>;
  const cls =
    status === 'PAID' ? 'bg-green-50 text-green-700 border-green-200' :
    status === 'NF_APPROVED' ? 'bg-teal-50 text-teal-700 border-teal-200' :
    status === 'NF_REVIEW' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
    status === 'NF_PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    status === 'NF_REJECTED' ? 'bg-red-50 text-red-600 border-red-200' :
    'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', cls)}>
      {CYCLE_STATUS_LABELS[status] ?? status}
    </span>
  );
}
